import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Receipt } from "lucide-react";

interface CostCategory {
  id: string;
  name: string;
  category_type: string;
  is_recurring: boolean;
  default_frequency: string;
  sort_order: number;
}

export function CostCategoriesSettings() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    is_recurring: true,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("cost_categories")
        .select("*")
        .order("sort_order");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({ name: "", is_recurring: true });
    setDialogOpen(true);
  };

  const handleEdit = (cat: CostCategory) => {
    setEditingId(cat.id);
    setFormData({ name: cat.name, is_recurring: cat.is_recurring });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("cost_categories")
          .update({
            name: formData.name,
            is_recurring: formData.is_recurring,
            default_frequency: formData.is_recurring ? "monthly" : "one_time",
          })
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Kategorie aktualisiert");
      } else {
        const maxOrder = Math.max(...categories.map((c) => c.sort_order), 0);
        const { error } = await supabase.from("cost_categories").insert([
          {
            name: formData.name,
            category_type: formData.is_recurring ? "custom_recurring" : "custom_one_time",
            is_recurring: formData.is_recurring,
            default_frequency: formData.is_recurring ? "monthly" : "one_time",
            sort_order: maxOrder + 1,
          },
        ]);

        if (error) throw error;
        toast.success("Kategorie hinzugefügt");
      }

      setDialogOpen(false);
      loadCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie diese Kategorie wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("cost_categories").delete().eq("id", id);

      if (error) throw error;
      toast.success("Kategorie gelöscht");
      loadCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Receipt className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Kostenkategorien</CardTitle>
                <CardDescription>
                  Verwalten Sie die Kategorien für Mitarbeiterkosten (Tools, KFZ, etc.)
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" />
              Neue Kategorie
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell className="font-medium">{cat.name}</TableCell>
                  <TableCell>{cat.is_recurring ? "Monatlich" : "Einmalig"}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(cat)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Kategorie bearbeiten" : "Neue Kategorie"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Büromaterial"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Monatlich wiederkehrend</Label>
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_recurring: checked })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
